import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import { DATA_QUERY_KEY, snapshotMetadataSchema } from '@/core';
import { DataSnapshot, SnapshotID } from './model';

export interface DataSnapshotContextValue {
  storeSnapshot: (snapshot: DataSnapshot) => Promise<SnapshotID>;
  getSnapshot: (snapshotID: SnapshotID) => Promise<DataSnapshot>;
}

export const DataSnapshotContext = createContext<DataSnapshotContextValue | undefined>(undefined);

/**
 * Hook to access the DataSnapshotProvider context.
 * Returns the snapshot context methods (storeSnapshot, getSnapshot) or undefined if no provider is present.
 */
export function useDataSnapshotContext() {
  return useContext(DataSnapshotContext);
}

/*
 * Context for sharing a snapshots metadata
 */
export const DataSnapshotMetadataContext = createContext<DataSnapshot['metadata'] | undefined>(undefined);

export function useDataSnapshotMetadata() {
  return useContext(DataSnapshotMetadataContext);
}

export function useSnapshotQuery(snapshotId?: SnapshotID) {
  const snapshotContext = useDataSnapshotContext();

  const query = useQuery(
    [DATA_QUERY_KEY, 'snapshot', snapshotId],
    async () => {
      if (!snapshotContext) {
        throw new Error('DataSnapshotProvider context is not available');
      }
      return snapshotContext.getSnapshot(snapshotId!);
    },
    {
      enabled: snapshotId !== undefined,
      staleTime: Infinity,
    }
  );

  return query;
}

/**
 * Hook to fetch snapshot metadata by snapshotId.
 * Uses the same React Query cache key as SnapshotDataQueryProvider, so if the snapshot
 * is already cached, no additional network request will be made.
 */
export function useSnapshotMetadata(snapshotId: SnapshotID | undefined) {
  const snapshotQuery = useSnapshotQuery(snapshotId);

  const metadata = useMemo(() => {
    if (snapshotQuery.data === undefined) {
      return undefined;
    }

    if (snapshotQuery.error) {
      throw new Error('Error fetching snapshot metadata');
    }

    const { data: snapshot } = snapshotQuery;

    return snapshotMetadataSchema.parse(snapshot.metadata);
  }, [snapshotQuery]);

  return metadata;
}
